import { useEffect, useMemo, useState } from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import { Check, ChevronDown, ChevronRight, FileText, X, Eye, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useAdminUserLegalConsents,
  useFetchSignatureImage,
} from '@/hooks/useAdminUserLegalConsents';
import type {
  AdminUserLegalConsentSigned,
  AdminUserLegalConsentOutstanding,
} from '@/types/admin';

interface UserLegalConsentsCardProps {
  userId: string;
}

type Row =
  | { kind: 'signed'; row: AdminUserLegalConsentSigned }
  | { kind: 'outstanding'; row: AdminUserLegalConsentOutstanding };

export function UserLegalConsentsCard({ userId }: UserLegalConsentsCardProps): JSX.Element {
  const { data, isLoading, isError, error } = useAdminUserLegalConsents(userId);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [signatureRowId, setSignatureRowId] = useState<string | null>(null);

  // Outstanding rows first so missing consents surface immediately, then
  // signed rows by accepted_at DESC (server already sorted those).
  const rows = useMemo<Row[]>(() => {
    if (!data) return [];
    return [
      ...data.outstanding.map((r) => ({ kind: 'outstanding' as const, row: r })),
      ...data.signed.map((r) => ({ kind: 'signed' as const, row: r })),
    ];
  }, [data]);

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">Legal agreements</CardTitle>
        {data && (
          <Badge variant="outline" className="font-mono">
            {data.summary.required_signed} / {data.summary.required_total} required
          </Badge>
        )}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : isError ? (
          <div className="text-sm text-destructive">
            Failed to load legal agreements
            {error instanceof Error ? `: ${error.message}` : ''}
          </div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-muted-foreground">
            No legal documents to show.
          </div>
        ) : (
          <div className="space-y-2">
            {rows.map((entry) => (
              <ConsentRow
                key={
                  entry.kind === 'signed'
                    ? entry.row.signed_id
                    : `outstanding-${entry.row.document_id}`
                }
                entry={entry}
                isExpanded={
                  entry.kind === 'signed' && expanded.has(entry.row.signed_id)
                }
                onToggle={
                  entry.kind === 'signed'
                    ? () => toggleExpand(entry.row.signed_id)
                    : undefined
                }
                onViewSignature={
                  entry.kind === 'signed' && entry.row.has_signature_image
                    ? () => setSignatureRowId(entry.row.signed_id)
                    : undefined
                }
              />
            ))}
          </div>
        )}
      </CardContent>
      <SignatureDialog
        signedId={signatureRowId}
        onClose={() => setSignatureRowId(null)}
      />
    </Card>
  );
}

interface ConsentRowProps {
  entry: Row;
  isExpanded: boolean;
  onToggle?: () => void;
  onViewSignature?: () => void;
}

function ConsentRow({
  entry,
  isExpanded,
  onToggle,
  onViewSignature,
}: ConsentRowProps): JSX.Element {
  if (entry.kind === 'outstanding') {
    const r = entry.row;
    return (
      <div className="flex items-center gap-3 rounded-md border border-amber-500/40 bg-amber-500/5 px-3 py-2">
        <X className="h-4 w-4 text-amber-600 shrink-0" aria-hidden />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">
            {r.document_name}
            <span className="text-xs font-normal text-muted-foreground ml-2">
              v{r.latest_version}
            </span>
          </div>
          <div className="text-xs text-muted-foreground">
            Outstanding · {r.signature_type === 'digital' ? 'Digital signature' : 'Click-to-accept'} required
          </div>
        </div>
        <Badge variant="outline" className="border-amber-600/50 text-amber-700">
          Outstanding
        </Badge>
      </div>
    );
  }

  const r = entry.row;
  return (
    <div className="rounded-md border border-border">
      <div className="flex items-center gap-3 px-3 py-2">
        <Check className="h-4 w-4 text-emerald-600 shrink-0" aria-hidden />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">
            {r.document_name}
            <span className="text-xs font-normal text-muted-foreground ml-2">
              v{r.version}
            </span>
          </div>
          <div
            className="text-xs text-muted-foreground"
            title={format(new Date(r.accepted_at), 'PPpp')}
          >
            Signed {formatDistanceToNow(new Date(r.accepted_at), { addSuffix: true })}
            {' · '}
            {r.signature_type === 'digital' ? 'Digital signature' : 'Click-to-accept'}
          </div>
        </div>
        {onViewSignature && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={onViewSignature}
            className="h-7 px-2"
          >
            <Eye className="h-3.5 w-3.5 mr-1" />
            View
          </Button>
        )}
        {onToggle && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={onToggle}
            className="h-7 w-7 p-0"
            aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </Button>
        )}
      </div>
      {isExpanded && (
        <div className="border-t border-border px-3 py-2 bg-muted/30 text-xs space-y-1">
          <DetailRow label="Accepted at" value={format(new Date(r.accepted_at), 'PPpp')} />
          <DetailRow label="Document type" value={r.document_type} />
          <DetailRow label="Signed version ID" value={r.version_id} mono />
          <DetailRow label="IP address" value={r.ip_address ?? '—'} mono />
          <DetailRow label="User agent" value={r.user_agent ?? '—'} mono breakAll />
        </div>
      )}
    </div>
  );
}

interface DetailRowProps {
  label: string;
  value: string;
  mono?: boolean;
  breakAll?: boolean;
}

function DetailRow({ label, value, mono, breakAll }: DetailRowProps): JSX.Element {
  return (
    <div className="grid grid-cols-[120px_1fr] gap-2">
      <div className="text-muted-foreground">{label}</div>
      <div className={`${mono ? 'font-mono' : ''} ${breakAll ? 'break-all' : 'truncate'}`}>
        {value}
      </div>
    </div>
  );
}

interface SignatureDialogProps {
  signedId: string | null;
  onClose: () => void;
}

function SignatureDialog({ signedId, onClose }: SignatureDialogProps): JSX.Element {
  const fetchImage = useFetchSignatureImage();
  const open = signedId !== null;
  const fetchedSignedId = fetchImage.data?.signed_id;
  const mutate = fetchImage.mutate;

  useEffect(() => {
    if (open && signedId && fetchedSignedId !== signedId) {
      mutate(signedId);
    }
  }, [open, signedId, fetchedSignedId, mutate]);

  const dataURL = fetchImage.data?.signature_data?.dataURL ?? null;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Signature
          </DialogTitle>
          <DialogDescription>
            Captured at sign time. Render is best-effort — the underlying
            JSONB also contains stroke metadata.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-md border border-border bg-muted/30 p-4 flex items-center justify-center min-h-[160px]">
          {fetchImage.isPending ? (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          ) : fetchImage.isError ? (
            <div className="text-sm text-destructive">
              Failed to load signature.
            </div>
          ) : dataURL ? (
            <img
              src={dataURL}
              alt="User signature"
              className="max-w-full max-h-[300px] object-contain"
            />
          ) : (
            <div className="text-sm text-muted-foreground">
              No image data attached.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

